import { useState, useRef, useEffect } from 'react';
import { authService, User } from '../utils/auth.ts';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import '../styles/colors.css';

interface ProfileEditModalProps {
  user: User | null;
  onClose: () => void;
  onProfileUpdate: (updatedUser: User) => void;
}

const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  user,
  onClose,
  onProfileUpdate,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [loginId, setLoginId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [avatar, setAvatar] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(null);
  const [hasCurrentAvatar, setHasCurrentAvatar] = useState(false);
  const [loginIdError, setLoginIdError] = useState<string | null>(null);
  const [loginIdChecking, setLoginIdChecking] = useState(false);
  const [loginIdAvailable, setLoginIdAvailable] = useState<boolean | null>(
    null
  );
  const [initialLoginId, setInitialLoginId] = useState<string>(''); // 初期値を記録
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 初期化処理（モーダル表示時のみ実行）
  useEffect(() => {
    const initializeProfile = async () => {
      try {
        // プロフィール情報を取得
        const profile = await authService.getProfile();

        // フォームに最新の情報をセット（バリデーションが走らないように一度に設定）
        setDisplayName(profile.username || '');
        // ログインIDは別途設定（バリデーション回避）
        const currentLoginId = profile.loginId || '';
        setLoginId(currentLoginId);
        setInitialLoginId(currentLoginId); // 初期値を記録

        // アバター情報も取得
        const avatarResponse = await authService.authFetch('/profile/avatar');
        if (avatarResponse.ok) {
          const avatarData = await avatarResponse.json();
          setHasCurrentAvatar(avatarData.has_avatar);
          setCurrentAvatarUrl(avatarData.avatar_url);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        // エラーの場合はpropsのuserから取得（フォールバック）
        setDisplayName(user?.username || '');
        const fallbackLoginId = user?.loginId || '';
        setLoginId(fallbackLoginId);
        setInitialLoginId(fallbackLoginId); // 初期値を記録
      }
    };

    // モーダル初回表示時のみ実行
    initializeProfile();
    // 依存配列は空にしてモーダル初回表示時のみ実行
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ログインID の変更時のバリデーション
  useEffect(() => {
    setLoginIdError(null);
    setLoginIdAvailable(null);

    if (!loginId.trim()) return;

    // クライアント側バリデーション
    const validationError = authService.validateLoginId(loginId);
    if (validationError) {
      setLoginIdError(validationError);
      return;
    }

    // 初期値と同じ場合はチェック不要（変更されていない）
    if (loginId === initialLoginId) {
      setLoginIdAvailable(true);
      return;
    }

    // 可用性チェック（デバウンス）
    const timer = setTimeout(async () => {
      setLoginIdChecking(true);
      try {
        const result = await authService.checkLoginIdAvailability(loginId);
        setLoginIdAvailable(result.available);
        if (!result.available) {
          setLoginIdError('このログインIDは既に使用されています');
        }
      } catch (error) {
        setLoginIdError('ログインIDの確認に失敗しました');
      } finally {
        setLoginIdChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [loginId, initialLoginId]);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // ファイルサイズチェック（5MB以下）
      if (file.size > 5 * 1024 * 1024) {
        alert('ファイルサイズは5MB以下にしてください');
        return;
      }

      // ファイル形式チェック
      if (!file.type.startsWith('image/')) {
        alert('画像ファイルを選択してください');
        return;
      }

      setAvatar(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsLoading(true);
    try {
      // プロフィール更新API呼び出し
      const response = await authService.authFetch('/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: displayName.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'プロフィールの更新に失敗しました');
      }

      // ログインID設定（変更がある場合）
      if (loginId.trim() !== initialLoginId) {
        if (loginId.trim()) {
          // ログインIDを設定
          await authService.setLoginId(loginId.trim());
        }
      }

      // アバターアップロード（選択されている場合）
      if (avatar) {
        const formData = new FormData();
        formData.append('avatar', avatar);

        const avatarResponse = await authService.authFetch('/profile/avatar', {
          method: 'POST',
          body: formData,
        });

        if (!avatarResponse.ok) {
          const errorData = await avatarResponse.json();
          throw new Error(
            errorData.error || 'アバターのアップロードに失敗しました'
          );
        }

        // アバターアップロード成功時は現在のアバター情報を更新
        const avatarData = await avatarResponse.json();
        setCurrentAvatarUrl(avatarData.avatar_url);
        setHasCurrentAvatar(true);
      }

      // 最新のプロフィール情報を取得してローカル情報を更新
      try {
        const latestProfile = await authService.getProfile();
        const updatedUser = authService.updateLocalUser(latestProfile);

        if (updatedUser) {
          onProfileUpdate(updatedUser);
        }
      } catch (error) {
        // エラーの場合はフォームの値で更新
        const updatedUser = authService.updateLocalUser({
          username: displayName.trim(),
          loginId: loginId.trim() || undefined,
        });

        if (updatedUser) {
          onProfileUpdate(updatedUser);
        }
      }
      onClose();
    } catch (error) {
      console.error('Profile update error:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'プロフィールの更新に失敗しました'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--overlay-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '1rem',
      }}
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          maxWidth: '400px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: '0 auto',
        }}
      >
        {/* ヘッダー */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem 1.5rem 1rem',
            borderBottom: '1px solid var(--border-light)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <PersonIcon style={{ color: 'var(--primary-600)' }} />
            プロフィール編集
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-secondary)',
              transition: 'all 0.2s ease-in-out',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--neutral-100)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* フォーム */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          {/* アバター選択 */}
          <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            <div
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                backgroundColor: 'var(--neutral-100)',
                border: '2px dashed var(--border-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                backgroundImage: previewUrl
                  ? `url(${previewUrl})`
                  : hasCurrentAvatar && currentAvatarUrl
                    ? `url(${currentAvatarUrl})`
                    : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
              }}
              onClick={() => fileInputRef.current?.click()}
              onMouseEnter={e => {
                if (!previewUrl && !hasCurrentAvatar) {
                  e.currentTarget.style.borderColor = 'var(--primary-500)';
                  e.currentTarget.style.backgroundColor = 'var(--primary-50)';
                }
              }}
              onMouseLeave={e => {
                if (!previewUrl && !hasCurrentAvatar) {
                  e.currentTarget.style.borderColor = 'var(--border-light)';
                  e.currentTarget.style.backgroundColor = 'var(--neutral-100)';
                }
              }}
            >
              {!previewUrl && !hasCurrentAvatar && (
                <PhotoCameraIcon
                  style={{
                    fontSize: '2rem',
                    color: 'var(--neutral-400)',
                  }}
                />
              )}
              {/* カメラアイコンオーバーレイ（既存画像がある場合） */}
              {(hasCurrentAvatar || previewUrl) && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: 'var(--primary-600)',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                  }}
                >
                  <PhotoCameraIcon
                    style={{
                      fontSize: '0.875rem',
                      color: 'white',
                    }}
                  />
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
            <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
              }}
            >
              クリックしてアバターを選択
            </p>
            <p
              style={{
                margin: '0.25rem 0 0',
                fontSize: '0.75rem',
                color: 'var(--neutral-500)',
              }}
            >
              JPG, PNG, GIF (5MB以下)
            </p>
          </div>

          {/* 表示名 */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: 'var(--text-primary)',
              }}
            >
              表示名
            </label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              required
              maxLength={50}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                transition: 'border-color 0.2s ease-in-out',
                outline: 'none',
              }}
              onFocus={e => {
                e.target.style.borderColor = 'var(--primary-500)';
              }}
              onBlur={e => {
                e.target.style.borderColor = 'var(--border-light)';
              }}
              placeholder="表示名を入力してください"
            />
            <p
              style={{
                margin: '0.25rem 0 0',
                fontSize: '0.75rem',
                color: 'var(--neutral-500)',
              }}
            >
              {displayName.length}/50文字
            </p>
          </div>

          {/* ログインID設定 */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: 'var(--text-primary)',
              }}
            >
              ログインID（任意）
            </label>
            <input
              type="text"
              value={loginId}
              onChange={e => setLoginId(e.target.value)}
              maxLength={20}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${
                  loginIdError ? 'var(--error)' : 'var(--border-light)'
                }`,
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                transition: 'border-color 0.2s ease-in-out',
                outline: 'none',
              }}
              onFocus={e => {
                if (!loginIdError) {
                  e.target.style.borderColor = 'var(--primary-500)';
                }
              }}
              onBlur={e => {
                if (!loginIdError) {
                  e.target.style.borderColor = 'var(--border-light)';
                }
              }}
              placeholder="英字で始まり、英数字とアンダースコアのみ"
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '0.25rem',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {loginIdChecking && (
                  <span
                    style={{ fontSize: '0.75rem', color: 'var(--warning)' }}
                  >
                    確認中...
                  </span>
                )}
                {loginIdError && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--error)' }}>
                    {loginIdError}
                  </span>
                )}
                {loginIdAvailable === true &&
                  loginId.trim() &&
                  !loginIdError && (
                    <span
                      style={{ fontSize: '0.75rem', color: 'var(--success)' }}
                    >
                      ✓ 利用可能
                    </span>
                  )}
              </div>
              <span
                style={{ fontSize: '0.75rem', color: 'var(--neutral-500)' }}
              >
                {loginId.length}/20文字
              </span>
            </div>
            <p
              style={{
                margin: '0.25rem 0 0',
                fontSize: '0.75rem',
                color: 'var(--neutral-500)',
              }}
            >
              設定するとブラウザのパスワード保存機能が使えます
            </p>
            {user?.loginIdLastChanged && (
              <p
                style={{
                  margin: '0.25rem 0 0',
                  fontSize: '0.75rem',
                  color: 'var(--neutral-500)',
                }}
              >
                前回変更:{' '}
                {new Date(user.loginIdLastChanged).toLocaleDateString('ja-JP')}
              </p>
            )}
          </div>

          {/* ボタン */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease-in-out',
                opacity: isLoading ? 0.6 : 1,
              }}
              onMouseEnter={e => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = 'var(--neutral-100)';
                }
              }}
              onMouseLeave={e => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={
                isLoading ||
                !displayName.trim() ||
                loginIdError !== null ||
                loginIdChecking
              }
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                background: 'var(--gradient-primary)',
                color: 'var(--neutral-50)',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor:
                  isLoading ||
                  !displayName.trim() ||
                  loginIdError !== null ||
                  loginIdChecking
                    ? 'not-allowed'
                    : 'pointer',
                transition: 'all 0.2s ease-in-out',
                opacity:
                  isLoading ||
                  !displayName.trim() ||
                  loginIdError !== null ||
                  loginIdChecking
                    ? 0.6
                    : 1,
                boxShadow: 'var(--shadow-md)',
              }}
              onMouseEnter={e => {
                if (
                  !isLoading &&
                  displayName.trim() &&
                  !loginIdError &&
                  !loginIdChecking
                ) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                }
              }}
              onMouseLeave={e => {
                if (
                  !isLoading &&
                  displayName.trim() &&
                  !loginIdError &&
                  !loginIdChecking
                ) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                }
              }}
            >
              {isLoading ? '更新中...' : '更新'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileEditModal;
